/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, QueryClient, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { apiVersions, getLogicEndpoint } from '@/services/endpoints';
import { AccessResponse, APPLICATION_PERMISSIONS, PROJECT_PERMISSIONS, roles } from '@/types/permissions';
import { getAutomationProject } from '@/services/projectService';
import { parseJsonBody } from '@/services/apiError';

/**
 * Extract the `oid` (Object ID) claim from a JWT access token.
 *
 * In multi-tenant scenarios, `account.localAccountId` may hold a guest OID from
 * a different tenant than the one the Otto control plane expects. The OID inside
 * the actual Otto access token is always tenant-correct because the token is
 * issued by the authority that matches the Otto resource.
 */
function getOidFromToken(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // JWT payloads are base64url-encoded (-/_ instead of +//, and no padding).
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = JSON.parse(atob(padded));
    return decoded.oid ?? null;
  } catch {
    return null;
  }
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const userPermissionKeys = {
  all: ['userPermissions'] as const,
  searchUsers: (searchText: string) => ['searchUsers', searchText.toLowerCase()] as const,
  usersWithAccess: (projectName: string, appName?: string) => 
    ['usersWithAccess', projectName.toLowerCase(), appName?.toLowerCase()] as const,
  checkAccess: (principalId: string | null, projectId: string, appName?: string) =>
    ['checkAccess', principalId?.toLowerCase(), projectId.toLowerCase(), appName?.toLowerCase()] as const,
  hasPermission: (principalId: string | null, projectName: string, appName: string | undefined, permission: string) =>
    ['hasPermission', principalId?.toLowerCase(), projectName.toLowerCase(), appName?.toLowerCase(), permission] as const,
};

/**
 * Invalidates all user permission queries for a given project/app scope.
 * Call this after adding, updating, or removing user access.
 */
export const invalidateUserPermissions = async (
  queryClient: QueryClient,
  userId: string,
  projectName: string,
  appName?: string
) => {
  await Promise.all([
    queryClient.invalidateQueries({ 
      queryKey: userPermissionKeys.usersWithAccess(projectName, appName) 
    }),
    queryClient.invalidateQueries({ 
      queryKey: userPermissionKeys.checkAccess(userId, projectName, appName),
    }),
  ]);
};

// =============================================================================
// TYPES
// =============================================================================

export interface User {
  id: string;
  displayName: string;
  email: string;
  userPrincipalName: string;
  type: 'user' | 'group' | 'application' | 'unknown';
}

/**
 * Searches for users, groups, and applications by name or email.
 * Returns a list of matching principals.
 */
export const useSearchUsers = (searchText: string) => {
  const { isAuthenticated, getGraphToken } = useAuth();

  return useQuery({
    queryKey: userPermissionKeys.searchUsers(searchText),
    queryFn: async (): Promise<User[]> => {
      const text = searchText.trim();
      const accessToken = await getGraphToken();

      if (!accessToken) {
        throw new Error('No access token available for Microsoft Graph', { cause: 'AuthenticationError' });
      }

      const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      // Search users, groups, and applications in parallel
      const userQuery = `startswith(displayName,'${text}') or startswith(givenName,'${text}') or startswith(surname,'${text}') or startswith(mail,'${text}') or startswith(userPrincipalName,'${text}')`;
      const groupQuery = `startswith(displayName,'${text}') or startswith(mail,'${text}')`;
      const appQuery = `startswith(displayName,'${text}')`;

      const [usersResponse, groupsResponse, appsResponse] = await Promise.all([
        fetch(`https://graph.microsoft.com/v1.0/users?$filter=${encodeURIComponent(userQuery)}&$top=50`, {
          method: 'GET',
          headers,
        }),
        fetch(`https://graph.microsoft.com/v1.0/groups?$filter=${encodeURIComponent(groupQuery)}&$top=50`, {
          method: 'GET',
          headers,
        }),
        fetch(`https://graph.microsoft.com/v1.0/applications?$filter=${encodeURIComponent(appQuery)}&$top=50`, {
          method: 'GET',
          headers,
        }),
      ]);

      const results: User[] = [];

      // Process users
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        results.push(...usersData.value.map((u: any) => getUserDetails(u, 'user')));
      }

      // Process groups
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        results.push(...groupsData.value.map((g: any) => getUserDetails(g, 'group')));
      }

      // Process applications
      if (appsResponse.ok) {
        const appsData = await appsResponse.json();
        results.push(...appsData.value.map((a: any) => getUserDetails(a, 'application')));
      }

      // If all requests failed, throw an error
      if (!usersResponse.ok && !groupsResponse.ok && !appsResponse.ok) {
        const errorText = await usersResponse.text();
        throw new Error(`Failed to search directory: ${usersResponse.status} ${errorText}`, { cause: 'GraphAPIError' });
      }

      return results;
    },
    enabled: isAuthenticated && searchText.length >= 1,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Fetches the list of users who have access to the specified project/app along with their permissions.
 * Returns an array of users with their corresponding access details.
 * Throws an error if the API call fails or if authentication is required.
 */
export const useUsersWithAccess = (projectName: string, appName: string | undefined) => {
  const { isAuthenticated, getOttoToken, getGraphToken } = useAuth();

  return useQuery({
    queryKey: userPermissionKeys.usersWithAccess(projectName, appName),
    queryFn: async (): Promise<{ user: User; access: AccessResponse }[]> => {
      const accessToken = await getOttoToken();
      if (!accessToken) {
        throw new Error('No access token available for Otto API', { cause: 'AuthenticationError' });
      }
      const scope = appName ? `/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}` : `/automationProjects/${encodeURIComponent(projectName)}`;
      const response = await fetch(`${getLogicEndpoint()}${scope}/principals?api-version=${apiVersions.otto}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch users with access: ${response.status} ${errorText}`, { cause: 'UsersWithAccessAPIError' });
      }

      const data = await response.json();
      const allUsers: { id: string; access: AccessResponse }[] = data.value?.map((item: any) => {
        const userId = item.name.split('_').slice(-1)[0];
        return { id: userId, access: getAccessPermission(!!appName, item.properties) };
      });
      const allUserIds = allUsers.map(user => user.id);

      if (allUserIds.length === 0) {
        return [];
      }
      
      const graphToken = await getGraphToken();
      if (!graphToken) {
        throw new Error('No access token available for Microsoft Graph', { cause: 'AuthenticationError' });
      }
      const userDetailsResponse = await fetch(`https://graph.microsoft.com/v1.0/directoryObjects/getByIds`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${graphToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: allUserIds, types: ['user', 'group', 'application'] }),
      });

      if (!userDetailsResponse.ok) {
        const errorText = await userDetailsResponse.text();
        throw new Error(`Failed to fetch user details from Graph: ${userDetailsResponse.status} ${errorText}`, { cause: 'GraphAPIError' });
      }

      const userDetails = (await userDetailsResponse.json())?.value || [];
      return allUsers.map((user) => {
        const details = userDetails.find((u: any) => u.id === user.id);
        return {
          user: details ? getUserDetails(details) : { id: user.id, displayName: user.id, email: 'Unknown', userPrincipalName: '', type: 'unknown' },
          access: user.access,
        };
      });
    },
    enabled: isAuthenticated && !!projectName,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Fetches the permissions for a specific user.
 */
export const useCheckAccess = (
  projectId: string,
  appName: string | undefined,
  userId: string | null,
  options?: { enabled?: boolean },
) => {
  const { isAuthenticated, getOttoToken, account } = useAuth();
  const tenantId = account?.tenantId;
  const effectiveUserId = userId === null ? account?.localAccountId : userId;
  const isPrincipalIdValid = !!effectiveUserId;
  const principalId = isPrincipalIdValid ? effectiveUserId : null;
  const callerEnabled = options?.enabled ?? true;

  return useQuery({
    queryKey: userPermissionKeys.checkAccess(principalId, projectId, appName),
    queryFn: async (): Promise<AccessResponse> => {
      // Call the internal fetch function directly to avoid deadlock with fetchQuery
      return fetchUserAccess(tenantId!, userId, projectId, appName, getOttoToken);      
    },
    enabled: callerEnabled && isAuthenticated && isPrincipalIdValid && !!projectId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Checks access for a specific user by calling the backend API.
 * Returns the user's role and permissions for the project/app.
 * Throws an error if the API call fails or if authentication is required.
 */
export const checkUserAccess = async (queryClient: QueryClient, tenantId: string, userId: string, projectName: string, appName: string | undefined, getAccessToken: () => Promise<string | null>) => {
  const noAccess = {
      role: 'No access',
      permissions: [],
  };

  const isPrincipalIdValid = !!userId;
  const principalId = isPrincipalIdValid ? userId : null;

  if (!isPrincipalIdValid) {
    return noAccess;
  }

  return queryClient.fetchQuery({
    queryKey: userPermissionKeys.checkAccess(principalId, projectName, appName),
    queryFn: () => fetchUserAccess(tenantId, userId, projectName, appName, getAccessToken),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });    
};

/**
 * Internal function that performs the actual access check API call.
 * Used by both useCheckAccess (directly) and checkUserAccess (via fetchQuery).
 */
const fetchUserAccess = async (
  _tenantId: string, 
  userId: string | null, 
  projectName: string, 
  appName: string | undefined, 
  getAccessToken: () => Promise<string | null>
): Promise<AccessResponse> => {
  const noAccess = {
    role: 'No access',
    permissions: [],
  };

  // Declared outside the try so the catch logs the principal that was actually
  // queried (tokenOid may be used when the caller passes null to mean "current user").
  let principalId = userId;

  try {
    const accessToken = await getAccessToken();

    // No token means we cannot authenticate to the principals endpoint. Short-circuit to
    // "no access" instead of issuing a guaranteed-failing request with "Authorization: Bearer null"
    // (which just produces noisy warnings).
    if (!accessToken) {
      console.warn('No access token available when checking user access');
      return noAccess;
    }

    if (principalId === null) {
      // In multi-tenant scenarios, account.localAccountId may be a guest OID from
      // a different tenant. The OID in the Otto token is always correct for the
      // Otto control plane, so prefer it over the passed-in userId.
      const tokenOid = getOidFromToken(accessToken);
      principalId = tokenOid ?? userId;
    }

    if (!principalId) {
      return noAccess;
    }

    const scope = appName ? `/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}` : `/automationProjects/${encodeURIComponent(projectName)}`;
    const response = await fetch(`${getLogicEndpoint()}${scope}/principals/${encodeURIComponent(principalId)}?api-version=${apiVersions.otto}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorCode = 'UnknownError';
      const parsed = errorText ? parseJsonBody(errorText) : null;
      if (parsed && typeof parsed === 'object') {
        errorCode = (parsed as Record<string, any>)?.error?.code ?? 'UnknownError';
      }
      if (response.status === 404 || (response.status === 403 && errorCode === 'AutomationAuthorizationFailed')) {
        // User has no access
        return noAccess;
      }
      
      throw new Error(`Failed to check access: ${response.status} ${errorText}`, { cause: 'CheckAccessAPIError' });
    }

    const data = await response.json();
    return getAccessPermission(!!appName, data.properties);
  } catch (error: unknown) {
    // Use console.warn instead of console.error — in local/E2E mode the principals
    // endpoint may not exist, and E2E test fixtures flag console.error as test failures.
    console.warn('Error checking access for user', principalId, error);
    return noAccess;
  }
};

/**
 * Fetches a user's object ID from Microsoft Graph by their email address.
 * Returns null if the user cannot be found or if there's an error.
 */
const fetchUserIdByEmail = async (
  email: string,
  getGraphToken: () => Promise<string | null>
): Promise<string | null> => {
  try {
    const accessToken = await getGraphToken();
    if (!accessToken) {
      console.warn('No access token available for Microsoft Graph when looking up user by email');
      return null;
    }

    // Try direct lookup by userPrincipalName or mail
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/users?$filter=${encodeURIComponent(`userPrincipalName eq '${email}' or mail eq '${email}'`)}&$select=id,displayName,mail,userPrincipalName`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      console.warn(`Failed to lookup user by email: ${response.status}`);
      return null;
    }

    const data = await response.json();
    if (data.value && data.value.length > 0) {
      return data.value[0].id;
    }

    return null;
  } catch (error) {
    console.warn('Error looking up user by email', email, error);
    return null;
  }
};

/**
 * Checks if the current user is the project owner.
 */
export const useIsProjectOwner = (projectName: string) => {
  const { account, getOttoToken, getGraphToken } = useAuth();
  const userId = account?.localAccountId ?? null;
  
  return useQuery({
    queryKey: ['isProjectOwner', userId, projectName],
    queryFn: async (): Promise<boolean> => {
      try {
        const project = await getAutomationProject(projectName, getOttoToken);
        const createdByEmail = project.systemData?.createdBy;
        
        if (!createdByEmail || !userId) {
          return false;
        }

        // Resolve the email to an object ID using Graph API
        const createdByUserId = await fetchUserIdByEmail(createdByEmail, getGraphToken);
        
        if (!createdByUserId) {
          // Fallback: compare as email if Graph lookup fails (e.g., guest users)
          // This may work if userId happens to match email format
          return createdByEmail.toLowerCase() === userId.toLowerCase();
        }
        
        return createdByUserId.toLowerCase() === userId.toLowerCase();
      } catch (error) {
        console.warn('Error checking if user is project owner', userId, error);
        return false;
      }
    },
    enabled: !!userId && !!projectName,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

}


type SupportedPermissions = 'Reader' | 'Contributor' | 'Author';
/**
 * Checks if the current logged in user has a given permission at a given scope
 * Returns true if the user has the permission, false otherwise. Also returns false if there's an error (e.g. network issue, API failure) to avoid blocking access due to transient issues.
 */
export const usePermission = (projectName: string, appName: string | undefined, permissions: SupportedPermissions[]) => {
  const { isAuthenticated, getOttoToken, account } = useAuth();
  const tenantId = account?.tenantId;
  const effectiveUserId = account ? account.localAccountId : null;
  const queryClient = useQueryClient();
  const isPrincipalIdValid = !!effectiveUserId;
  const principalId = isPrincipalIdValid ? effectiveUserId : null;

  return useQuery({
    queryKey: userPermissionKeys.hasPermission(principalId, projectName, appName, permissions.join(',')),
    queryFn: async (): Promise<boolean> => {
      return fetchUserHasPermission(queryClient, tenantId!, effectiveUserId!, projectName, appName, permissions, getOttoToken);
    },
    enabled: isAuthenticated && isPrincipalIdValid && !!projectName,
    staleTime: 1000 * 60 * 5, // 5 minutes — role changes should reflect quickly
  });
};

const fetchUserHasPermission = async (queryClient: QueryClient, tenantId: string, userId: string, projectName: string, appName: string | undefined, permissions: SupportedPermissions[], getAccessToken: () => Promise<string | null>): Promise<boolean> => {
  const isPrincipalIdValid = !!userId;
  const principalId = isPrincipalIdValid ? userId : null;
  
  if (!isPrincipalIdValid) {
    return false;
  }
  
  try {
    if (!appName) {
      const projectAccess = await checkUserAccess(queryClient, tenantId, userId, projectName, /* appName */undefined, getAccessToken);
      return permissions.includes(projectAccess.role as SupportedPermissions);
    }

    const appAccess = await checkUserAccess(queryClient, tenantId, userId, projectName, appName, getAccessToken);
    return permissions.includes(appAccess.role as SupportedPermissions);
  } catch (error: unknown) {
    console.warn('Error checking permission for user', principalId, error);
    return false;
  }
};

export const addOrUpdateUserAccess = async (principalId: string, projectName: string, appName: string | undefined, permissionId: string, getAccessToken: () => Promise<string | null>) => {
  try {
    const accessToken = await getAccessToken();
    const scope = appName ? `/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}` : `/automationProjects/${encodeURIComponent(projectName)}`;
    const response = await fetch(`${getLogicEndpoint()}${scope}/principals/${encodeURIComponent(principalId)}?api-version=${apiVersions.otto}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ properties: { permission: permissionId } }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add/update user access: ${response.status} ${errorText}`, { cause: 'AddUpdateAccessAPIError' });
    }
    return true;
  } catch (error: unknown) {
    console.error('Error adding/updating access for user', principalId, error);
    throw error;
  }
};

export const removeUserAccess = async (principalId: string, projectName: string, appName: string | undefined, getAccessToken: () => Promise<string | null>) => {
  try {
    const accessToken = await getAccessToken();
    const scope = appName ? `/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}` : `/automationProjects/${encodeURIComponent(projectName)}`;
    const response = await fetch(`${getLogicEndpoint()}${scope}/principals/${encodeURIComponent(principalId)}?api-version=${apiVersions.otto}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to remove user access: ${response.status} ${errorText}`, { cause: 'RemoveAccessAPIError' });
    }
    return true;
  } catch (error: unknown) {
    console.error('Error removing access for user', principalId, error);
    throw error;
  }
};

const getAccessPermission = (isAppScope: boolean, properties: any): AccessResponse => {
  const permissionType = properties?.permission;
  const permission = isAppScope   
    ? APPLICATION_PERMISSIONS.find(p => p.id === permissionType)
    : PROJECT_PERMISSIONS.find(p => p.id === permissionType);
  const role = roles[permissionType] || 'Access';
  return permission ? { role, permissions: [permission] } : { role: 'No access', permissions: [] };
};

const getUserDetails = (user: any, typeOverride?: User['type']): User => ({
  id: user.id,
  displayName: user.displayName,
  email: user.mail ?? '',
  userPrincipalName: user.userPrincipalName ?? '',
  type: typeOverride ?? (user['@odata.type'] ? user['@odata.type'].split('.').slice(-1)[0].toLowerCase() : 'unknown'),
});
